import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Tag, ChevronDown, ChevronRight, Star, ExternalLink, X, Check } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import DataTable from '../common/DataTable';
import StatusBadge from '../common/StatusBadge';
import Thumbnail from '../common/Thumbnail';
import { Modal, FormField, FileUpload, PrimaryButton, SecondaryButton } from '../common/FormComponents';
import { adsAPI, usersAPI, mainCategoriesAPI, locationsAPI } from '../../services/api';
import { AD_TYPES, AD_PLACEMENTS, PRIORITIES, MAX_AD_LINKS, COLORS, formatLabel } from '../../utils/constants';
import { adImage, adMediaList } from '../../utils/media';

const C = COLORS;

const AdsPage = () => {
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modal, setModal] = useState(null); // 'create' | 'assign' | {id}
  const [form, setForm] = useState({});
  const [files, setFiles] = useState([]);
  // Up to MAX_AD_LINKS destination URLs; exactly one carries is_active
  const [links, setLinks] = useState([{ url: '', is_active: true }]);
  const [priority, setPriority] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({});

  // Dependent-dropdown option lists
  const [mainOptions, setMainOptions] = useState([]);
  const [subOptions, setSubOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const res = await adsAPI.getAll({ page, limit: 10, ...filter });
      setData(res.data.data);
      setTotalPages(res.data.totalPages);
    } catch (err) { console.error(err); }
  }, [page, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { usersAPI.getAll({ limit: 100 }).then(r => setUsers(r.data.data)).catch(() => {}); }, []);

  // Load parent option lists once
  useEffect(() => {
    mainCategoriesAPI.getAll({ limit: 1000 })
      .then(r => setMainOptions(r.data.data.map(m => ({ value: m.id, label: m.name })))).catch(() => {});
    locationsAPI.getStates()
      .then(r => setStateOptions(r.data.map(s => ({ value: s.id, label: s.name })))).catch(() => {});
  }, []);

  // Dependent: Sub Category options follow the selected Main Category (load-only; never clears the child)
  useEffect(() => {
    if (!form.main_category_id) { setSubOptions([]); return; }
    mainCategoriesAPI.getById(form.main_category_id)
      .then(res => setSubOptions((res.data.subcategories || []).map(s => ({ value: s.id, label: s.name }))))
      .catch(() => setSubOptions([]));
  }, [form.main_category_id]);

  // Dependent: City options follow the selected State
  useEffect(() => {
    if (!form.state_id) { setCityOptions([]); return; }
    locationsAPI.getCities(form.state_id)
      .then(res => setCityOptions((res.data || []).map(c => ({ value: c.id, label: c.name }))))
      .catch(() => setCityOptions([]));
  }, [form.state_id]);

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  // Remove one stored creative. Applied immediately rather than on submit, so
  // the ad list and the mobile app stop showing it right away. The response is
  // the refreshed ad, so the modal re-renders from server truth.
  const handleRemoveMedia = async (mediaId) => {
    if (!form._id) return;
    if (!window.confirm('Remove this creative? The file is deleted from the server.')) return;
    setLoading(true);
    try {
      const res = await adsAPI.deleteMedia(form._id, mediaId);
      setForm(p => ({ ...p, _media: res.data?.media || [] }));
      fetchData();
    } catch (err) {
      console.error(err);
      window.alert(err?.response?.data?.error || 'Could not remove the creative.');
    } finally {
      setLoading(false);
    }
  };

  // ── Link slots ──────────────────────────────────────────────────────────
  const blankLinks = () => [{ url: '', is_active: true }];
  const setLinkUrl = (i, url) => setLinks(p => p.map((l, idx) => (idx === i ? { ...l, url } : l)));
  // Selecting an active link clears the flag on every other one, mirroring the
  // single-active rule the backend enforces on write.
  const setActiveLink = (i) => setLinks(p => p.map((l, idx) => ({ ...l, is_active: idx === i })));
  const addLink = () => setLinks(p => (p.length >= MAX_AD_LINKS ? p : [...p, { url: '', is_active: false }]));
  const removeLink = (i) => setLinks(p => {
    const next = p.filter((_, idx) => idx !== i);
    if (!next.length) return blankLinks();
    // Removing the active slot promotes the first remaining one
    if (!next.some(l => l.is_active)) next[0] = { ...next[0], is_active: true };
    return next;
  });

  // Switch the active link straight from the table, without reopening the form
  const handleActivate = async (adId, linkId) => {
    try { await adsAPI.activateLink(adId, linkId); fetchData(); }
    catch (err) { console.error(err); alert('Failed to change the active link.'); }
  };
  // On user change of a parent, reset its dependent child so it can't hold a stale value
  const handleMainChange = (e) => setForm(p => ({ ...p, main_category_id: e.target.value, subcategory_id: '' }));
  const handleStateChange = (e) => setForm(p => ({ ...p, state_id: e.target.value, city_id: '' }));

  const handleSubmit = async () => {
    if (!form.title) return;
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && !k.startsWith('_') && fd.append(k, v));
      // form-data carries strings only, so the link set travels as JSON
      const filled = links.filter(l => l.url.trim()).map(l => ({ url: l.url.trim(), is_active: !!l.is_active }));
      // A link set always has exactly one active entry; fall back to the first
      const payloadLinks = filled.some(l => l.is_active)
        ? filled
        : filled.map((l, i) => ({ ...l, is_active: i === 0 }));
      fd.append('links', JSON.stringify(payloadLinks));
      if (priority) fd.append('priority', priority);
      files.forEach(f => fd.append('media', f));

      if (form._id) {
        await adsAPI.update(form._id, fd);
      } else {
        await adsAPI.create(fd);
      }
      setModal(null); setForm({}); setFiles([]); setLinks(blankLinks()); setPriority(null); fetchData();
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleAssign = async () => {
    if (!form.user_id) return;
    setLoading(true);
    try {
      await adsAPI.assign(form._adId, { user_id: form.user_id, priority: form.assign_priority, notes: form.notes });
      setModal(null); setForm({}); fetchData();
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete ad "${row.title}"?`)) return;
    try { await adsAPI.delete(row.id); fetchData(); } catch (err) { alert('Failed'); }
  };

  const columns = [
    { key: 'media', label: 'Creative', render: (v, row) => {
      const items = adMediaList(row);
      const lead = adImage(row) || items[0]?.url;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Thumbnail src={lead} alt={row.title} isVideo={!adImage(row) && !!items[0]?.isVideo} />
          {items.length > 1 && <span style={{ fontSize: 11, color: C.g400 }}>+{items.length - 1}</span>}
        </div>
      );
    }},
    { key: 'title', label: 'Campaign', render: (v, row) => row.active_link ? (
      <a href={row.active_link} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} title={row.active_link}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 600, color: C.primary, textDecoration: 'none' }}>
        {v} <ExternalLink size={12} />
      </a>
    ) : <span style={{ fontWeight: 600, color: C.g800 }}>{v}</span> },
    { key: 'subcategory_name', label: 'Sub Category', render: (v, row) => {
      const label = v || row.main_category_name;
      return label ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: C.g600, background: C.g100, padding: '3px 10px', borderRadius: 6 }}>
          <Tag size={11} />{label}
        </span>
      ) : <span style={{ fontSize: 12, color: C.g400 }}>—</span>;
    }},
    { key: 'priority', label: 'Priority', render: v => <StatusBadge status={v} /> },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
    { key: 'impressions', label: 'Impressions', render: v => (v || 0).toLocaleString() },
    { key: 'clicks', label: 'Clicks', render: v => (v || 0).toLocaleString() },
    { key: 'assigned_to_name', label: 'Assigned To', render: v => (
      <span style={{ color: v ? C.g700 : C.g400, fontStyle: v ? 'normal' : 'italic' }}>{v || 'Unassigned'}</span>
    )},
  ];

  const priorityOptions = [
    { label: 'Low', color: C.info, bg: C.infoLight, icon: <ChevronDown size={14} /> },
    { label: 'Medium', color: C.warning, bg: C.warningLight, icon: <ChevronRight size={14} /> },
    { label: 'High', color: C.danger, bg: C.dangerLight, icon: <Star size={14} /> },
  ];

  const detail = (label, value) => (
    <div><span style={{ fontSize: 12, color: C.g400 }}>{label}</span>
      <p style={{ margin: '4px 0 0', fontWeight: 600, color: C.g800 }}>{value || '—'}</p></div>
  );

  return (
    <AppLayout title="Ads Management">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.g900 }}>Ads</h2>
        <PrimaryButton onClick={() => { setForm({}); setFiles([]); setLinks(blankLinks()); setPriority(null); setModal('create'); }} icon={<Plus size={18} />}>Create Ad</PrimaryButton>
      </div>

      <DataTable columns={columns} data={data} page={page} totalPages={totalPages}
        onPageChange={setPage} searchPlaceholder="Search ads..."
        filterOptions={['All', 'Active', 'Paused', 'Scheduled', 'Expired']}
        onSearch={s => setFilter(p => ({ ...p, search: s }))}
        onFilter={f => setFilter(f === 'All' ? {} : { status: f.toLowerCase() })}
        onEdit={row => {
          setForm({
            _id: row.id, title: row.title, description: row.description, ad_type: row.ad_type,
            ad_placement: row.ad_placement, main_category_id: row.main_category_id || '', subcategory_id: row.subcategory_id || '',
            state_id: row.state_id || '', city_id: row.city_id || '', ad_width: row.ad_width, ad_height: row.ad_height,
            status: row.status, start_date: row.start_date?.split('T')[0], end_date: row.end_date?.split('T')[0],
            _media: row.media || [],
          });
          setPriority(row.priority); setFiles([]);
          setLinks(row.links?.length
            ? row.links.map(l => ({ id: l.id, url: l.url, is_active: !!l.is_active }))
            : blankLinks());
          setModal('create');
        }}
        onDelete={handleDelete}
        expandRow={row => (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <span style={{ fontSize: 12, color: C.g400 }}>Creatives ({adMediaList(row).length})</span>
              {adMediaList(row).length ? (
                <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                  {adMediaList(row).map(m => (
                    <a key={m.id} href={m.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                      title={m.isVideo ? 'Open video' : 'Open full size'} style={{ display: 'block', lineHeight: 0 }}>
                      <Thumbnail src={m.url} alt={row.title} isVideo={m.isVideo} width={92} height={64} />
                    </a>
                  ))}
                </div>
              ) : <p style={{ margin: '4px 0 0', fontWeight: 600, color: C.g800 }}>—</p>}
            </div>
            {detail('Main Category', row.main_category_name)}
            {detail('Sub Category', row.subcategory_name)}
            {detail('Placement', row.ad_placement ? formatLabel(row.ad_placement) : null)}
            {detail('Location', [row.city_name, row.state_name].filter(Boolean).join(', '))}
            {detail('Size', (row.ad_width || row.ad_height) ? `${row.ad_width || '?'} × ${row.ad_height || '?'}` : null)}
            <div style={{ minWidth: 260 }}>
              <span style={{ fontSize: 12, color: C.g400 }}>Links ({(row.links || []).length}/{MAX_AD_LINKS}) — the active one opens on click</span>
              {(row.links || []).length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                  {row.links.map(l => (
                    <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button onClick={e => { e.stopPropagation(); if (!l.is_active) handleActivate(row.id, l.id); }}
                        title={l.is_active ? 'Active link' : 'Set as the active link'}
                        style={{ width: 18, height: 18, flexShrink: 0, borderRadius: '50%', cursor: l.is_active ? 'default' : 'pointer',
                          border: `2px solid ${l.is_active ? C.success : C.g300}`, background: l.is_active ? C.success : '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                        {l.is_active && <Check size={11} color="#fff" />}
                      </button>
                      <a href={l.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} title={l.url}
                        style={{ fontSize: 13, color: l.is_active ? C.primary : C.g500, fontWeight: l.is_active ? 600 : 400,
                          textDecoration: 'none', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.url}
                      </a>
                    </div>
                  ))}
                </div>
              ) : <p style={{ margin: '4px 0 0', fontWeight: 600, color: C.g800 }}>—</p>}
            </div>
            <div><span style={{ fontSize: 12, color: C.g400 }}>Priority</span><p style={{ margin: '4px 0 0' }}><StatusBadge status={row.priority} /></p></div>
            <button onClick={() => { setForm({ _adId: row.id }); setModal('assign'); }}
              style={{ padding: '8px 16px', border: `1px solid ${C.primary}`, borderRadius: 8, background: C.primaryGhost, cursor: 'pointer', fontSize: 13, color: C.primary, fontWeight: 600, fontFamily: 'inherit' }}>
              Assign User
            </button>
          </div>
        )}
      />

      {/* Create/Edit Modal */}
      {modal === 'create' && (
        <Modal title={form._id ? 'Edit Ad' : 'Create New Ad'} onClose={() => setModal(null)} wide>
          <FormField label="Campaign Title" name="title" value={form.title} onChange={handleChange} required />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Ad Type" name="ad_type" value={form.ad_type} onChange={handleChange} options={AD_TYPES} />
            <FormField label="Ad Placement" name="ad_placement" value={form.ad_placement} onChange={handleChange} options={AD_PLACEMENTS} placeholder="Select placement..." />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Main Category" name="main_category_id" value={form.main_category_id} onChange={handleMainChange} options={mainOptions} placeholder="Select main category..." />
            <FormField label="Sub Category" name="subcategory_id" value={form.subcategory_id} onChange={handleChange} options={subOptions}
              placeholder={form.main_category_id ? 'Select sub category...' : 'Select a main category first'} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.g700 }}>Priority</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {priorityOptions.map(p => (
                  <button key={p.label} onClick={() => setPriority(p.label.toLowerCase())}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 8px',
                      border: `2px solid ${priority === p.label.toLowerCase() ? p.color : C.g200}`, borderRadius: 10, cursor: 'pointer',
                      fontSize: 13, fontWeight: priority === p.label.toLowerCase() ? 700 : 500,
                      color: priority === p.label.toLowerCase() ? p.color : C.g600,
                      background: priority === p.label.toLowerCase() ? p.bg : '#fff', fontFamily: 'inherit' }}>
                    {p.icon} {p.label}
                  </button>
                ))}
              </div>
            </div>
            <FormField label="Target Audience" name="target_audience" value={form.target_audience} onChange={handleChange}
              options={['all_users', 'new_users', 'premium_users', 'fleet_managers', 'individual_owners']} />
          </div>
          <FormField label="Description" name="description" value={form.description} onChange={handleChange} rows={3} />
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.g700, letterSpacing: 0.2 }}>
              Links / URLs <span style={{ fontWeight: 400, color: C.g400 }}>— up to {MAX_AD_LINKS}; pick the one that is active</span>
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {links.map((l, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => setActiveLink(i)} disabled={!l.url.trim()}
                    title={l.is_active ? 'Active link' : 'Set as the active link'}
                    style={{ width: 22, height: 22, flexShrink: 0, borderRadius: '50%', padding: 0,
                      border: `2px solid ${l.is_active ? C.success : C.g300}`, background: l.is_active ? C.success : '#fff',
                      cursor: l.url.trim() ? 'pointer' : 'not-allowed', opacity: l.url.trim() ? 1 : 0.5,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {l.is_active && <Check size={13} color="#fff" />}
                  </button>
                  <input value={l.url} onChange={e => setLinkUrl(i, e.target.value)}
                    placeholder={`Link ${i + 1} — e.g. https://example.com/offer`}
                    style={{ flex: 1, padding: '10px 14px', border: `1px solid ${l.is_active ? C.success : C.g200}`, borderRadius: 10,
                      fontSize: 14, outline: 'none', background: C.g50, boxSizing: 'border-box', fontFamily: 'inherit' }} />
                  <button onClick={() => removeLink(i)} title="Remove this link"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: C.g400, display: 'flex' }}>
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
            {links.length < MAX_AD_LINKS && (
              <button onClick={addLink}
                style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                  border: `1px dashed ${C.g300}`, borderRadius: 10, background: '#fff', cursor: 'pointer',
                  fontSize: 13, color: C.g600, fontFamily: 'inherit' }}>
                <Plus size={14} /> Add link
              </button>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="State" name="state_id" value={form.state_id} onChange={handleStateChange} options={stateOptions} placeholder="Select state..." />
            <FormField label="City" name="city_id" value={form.city_id} onChange={handleChange} options={cityOptions}
              placeholder={form.state_id ? 'Select city...' : 'Select a state first'} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Ad Width" name="ad_width" value={form.ad_width} onChange={handleChange} placeholder="e.g. 300, 100%, auto" />
            <FormField label="Ad Height" name="ad_height" value={form.ad_height} onChange={handleChange} placeholder="e.g. 250, 100%, auto" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Start Date" name="start_date" type="date" value={form.start_date} onChange={handleChange} />
            <FormField label="End Date" name="end_date" type="date" value={form.end_date} onChange={handleChange} />
          </div>
          {form._id && <FormField label="Status" name="status" value={form.status} onChange={handleChange} options={['active', 'paused', 'scheduled', 'expired']} />}
          {!!form._media?.length && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.g700 }}>Current Creatives</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {adMediaList({ media: form._media }).map(m => (
                  <div key={m.id} style={{ position: 'relative', lineHeight: 0 }}>
                    <a href={m.url} target="_blank" rel="noreferrer" title="Open full size" style={{ display: 'block', lineHeight: 0 }}>
                      <Thumbnail src={m.url} alt={form.title} isVideo={m.isVideo} width={92} height={64} radius={10} />
                    </a>
                    <button
                      type="button"
                      onClick={() => handleRemoveMedia(m.id)}
                      disabled={loading}
                      title="Remove creative"
                      aria-label="Remove creative"
                      style={{
                        position: 'absolute', top: -8, right: -8, width: 24, height: 24,
                        borderRadius: '50%', border: 'none', cursor: loading ? 'default' : 'pointer',
                        background: C.danger, color: '#fff', fontSize: 14, lineHeight: '24px',
                        padding: 0, boxShadow: '0 1px 4px rgba(0,0,0,.3)', opacity: loading ? 0.6 : 1,
                      }}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: C.g400 }}>
                New uploads are added alongside these. Use &times; to remove one.
              </p>
            </div>
          )}
          <FileUpload label="Creative Assets (Images & Videos)" multiple files={files}
            onChange={f => setFiles(prev => [...prev, ...Array.from(f)])}
            onRemove={i => setFiles(prev => prev.filter((_, idx) => idx !== i))} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <SecondaryButton onClick={() => setModal(null)}>Cancel</SecondaryButton>
            <PrimaryButton onClick={handleSubmit} disabled={loading}>{loading ? 'Saving...' : form._id ? 'Update' : 'Create'}</PrimaryButton>
          </div>
        </Modal>
      )}

      {/* Assign Modal */}
      {modal === 'assign' && (
        <Modal title="Assign Ad to User" onClose={() => setModal(null)}>
          <FormField label="Select User" name="user_id" value={form.user_id} onChange={handleChange}
            options={users.map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` }))} required />
          <FormField label="Priority" name="assign_priority" value={form.assign_priority} onChange={handleChange} options={PRIORITIES} />
          <FormField label="Notes" name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Optional notes..." />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <SecondaryButton onClick={() => setModal(null)}>Cancel</SecondaryButton>
            <PrimaryButton onClick={handleAssign} disabled={loading}>{loading ? 'Assigning...' : 'Assign'}</PrimaryButton>
          </div>
        </Modal>
      )}
    </AppLayout>
  );
};

export default AdsPage;
